/**
 * ConferenceNetworkTab - Admin interface for managing Conference Networks
 * 
 * Features:
 * - Create/Edit/Delete conference networks
 * - Manage member schools with logos and athletic website links
 * - Configure custom menu items
 * - Link syndication sources to conferences
 */
import { useState, useEffect } from "react";
import { 
  Globe, Plus, Trash2, Edit2, Save, X, Users, Link2, Menu,
  ChevronDown, ChevronUp, ExternalLink, Palette, Image, Loader2,
  Table, RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConferenceNetworkTab = () => {
  const [networks, setNetworks] = useState([]);
  const [syndicationSources, setSyndicationSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [networksRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/conference-networks`),
        axios.get(`${API}/syndication/sources`, { headers: getAuthHeaders() })
      ]);
      setNetworks(networksRes.data);
      setSyndicationSources(sourcesRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNetwork = async (networkId) => {
    if (!confirm("Are you sure you want to delete this conference network?")) return;
    
    try {
      await axios.delete(`${API}/conference-networks/${networkId}`, { headers: getAuthHeaders() });
      setNetworks(networks.filter(n => n.id !== networkId));
    } catch (err) {
      alert("Error deleting network: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase text-white">Conference Networks</h2>
          <p className="text-zinc-500 text-sm">Create branded streaming networks for sports conferences</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Network
        </Button>
      </div>

      {/* Create/Edit Form */}
      {(showCreateForm || editingNetwork) && (
        <NetworkForm
          network={editingNetwork}
          syndicationSources={syndicationSources}
          onSave={(savedNetwork) => {
            if (editingNetwork) {
              setNetworks(networks.map(n => n.id === savedNetwork.id ? savedNetwork : n));
            } else {
              setNetworks([...networks, savedNetwork]);
            }
            setEditingNetwork(null);
            setShowCreateForm(false);
          }}
          onCancel={() => {
            setEditingNetwork(null);
            setShowCreateForm(false);
          }}
        />
      )}

      {/* Networks List */}
      {networks.length === 0 && !showCreateForm ? (
        <div className="bg-zinc-800/50 border border-white/10 p-8 text-center">
          <Globe className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-white font-bold mb-2">No Conference Networks</h3>
          <p className="text-zinc-500 text-sm mb-4">
            Create a conference network to set up a branded streaming page for a sports conference
          </p>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Network
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {networks.map((network) => (
            <div key={network.id} className="bg-zinc-800/50 border border-white/10 rounded-lg overflow-hidden">
              {/* Network Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {network.logo_url ? (
                    <img src={network.logo_url} alt="" className="h-12 w-12 object-contain" />
                  ) : (
                    <div 
                      className="h-12 w-12 rounded flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: network.primary_color }}
                    >
                      {network.name?.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-white font-bold">{network.name}</h3>
                    <p className="text-zinc-500 text-xs">
                      /network/{network.slug}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {network.member_schools?.length || 0} schools
                      </span>
                      <span className="flex items-center gap-1">
                        <Menu className="w-3 h-3" />
                        {network.menu_items?.length || 0} menu items
                      </span>
                      <span className="flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        {network.syndication_source_ids?.length || 0} sources
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Visibility Toggle */}
                  <button
                    onClick={async () => {
                      const newVisible = !(network.visible !== false);
                      try {
                        await axios.put(`${API}/conference-networks/${network.channel_id || network.id}`, { visible: newVisible }, { headers: getAuthHeaders() });
                        setNetworks(networks.map(n => n.id === network.id ? { ...n, visible: newVisible } : n));
                      } catch (e) {
                        alert("Failed to update visibility");
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase transition-colors ${
                      network.visible !== false
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-zinc-800 text-zinc-500 border border-white/10"
                    }`}
                    title={network.visible !== false ? "Visible in channel dropdown" : "Hidden from channel dropdown"}
                    data-testid={`visibility-toggle-${network.channel_id || network.id}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${network.visible !== false ? "bg-green-400" : "bg-zinc-600"}`} />
                    {network.visible !== false ? "Visible" : "Hidden"}
                  </button>
                  <a
                    href={`${window.location.origin}/network/${network.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-zinc-500 hover:text-white transition-colors"
                    title="View network page"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <Button
                    onClick={() => setEditingNetwork(network)}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white"
                    size="sm"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDeleteNetwork(network.id)}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Color Preview */}
              <div className="px-4 pb-4 flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded border border-white/20"
                  style={{ backgroundColor: network.primary_color }}
                  title="Primary color"
                />
                <div 
                  className="w-6 h-6 rounded border border-white/20"
                  style={{ backgroundColor: network.secondary_color }}
                  title="Secondary color"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Network Create/Edit Form
const NetworkForm = ({ network, syndicationSources, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: network?.name || "",
    slug: network?.slug || "",
    logo_url: network?.logo_url || "",
    primary_color: network?.primary_color || "#f97316",
    secondary_color: network?.secondary_color || "#000000",
    member_schools: network?.member_schools || [],
    menu_items: network?.menu_items || [],
    syndication_source_ids: network?.syndication_source_ids || [],
    fallback_video_url: network?.fallback_video_url || ""
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  
  // Standings config state
  const [standingsConfigs, setStandingsConfigs] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [newStanding, setNewStanding] = useState({ sport: "", source_url: "" });
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  
  // Scheduler state
  const [networkBroadcasts, setNetworkBroadcasts] = useState([]);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [showAddGame, setShowAddGame] = useState(false);
  const [newGame, setNewGame] = useState({
    title: "", sport: "Basketball", scheduled_date: "", scheduled_time: "",
    home_team_name: "", away_team_name: "", youtube_url: "", stats_link: ""
  });
  const [selectedBroadcasts, setSelectedBroadcasts] = useState(new Set());
  
  // File upload state
  const [uploading, setUploading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return { Authorization: `Bearer ${token}` };
  };
  
  // Fetch standings configs when editing
  useEffect(() => {
    if (network?.channel_id) {
      fetchStandingsConfigs();
      fetchNetworkBroadcasts();
    }
  }, [network?.channel_id]);
  
  const fetchStandingsConfigs = async () => {
    if (!network?.channel_id) return;
    setStandingsLoading(true);
    try {
      const res = await axios.get(`${API}/standings/configs/${network.channel_id}`, { headers: getAuthHeaders() });
      setStandingsConfigs(res.data || []);
    } catch (err) {
      console.error("Error fetching standings configs:", err);
    } finally {
      setStandingsLoading(false);
    }
  };
  
  const fetchNetworkBroadcasts = async () => {
    if (!network?.channel_id) return;
    setSchedulerLoading(true);
    try {
      const res = await axios.get(`${API}/conference-networks/${network.channel_id}/broadcasts`);
      setNetworkBroadcasts(res.data || []);
    } catch (err) {
      console.error("Error fetching network broadcasts:", err);
    } finally {
      setSchedulerLoading(false);
    }
  };
  
  const handleAddGame = async () => {
    if (!newGame.title || !newGame.scheduled_date || !newGame.scheduled_time) {
      alert("Title, date, and time are required");
      return;
    }
    try {
      await axios.post(`${API}/conference-networks/${network.channel_id}/broadcasts`, newGame, { headers: getAuthHeaders() });
      setNewGame({ title: "", sport: "Basketball", scheduled_date: "", scheduled_time: "", home_team_name: "", away_team_name: "", youtube_url: "", stats_link: "" });
      setShowAddGame(false);
      fetchNetworkBroadcasts();
    } catch (err) {
      alert("Error adding game: " + (err.response?.data?.detail || err.message));
    }
  };
  
  const handleDeleteNetworkBroadcast = async (broadcastId) => {
    if (!window.confirm("Delete this broadcast?")) return;
    try {
      await axios.delete(`${API}/conference-networks/${network.channel_id}/broadcasts/${broadcastId}`, { headers: getAuthHeaders() });
      fetchNetworkBroadcasts();
    } catch (err) {
      alert("Error deleting: " + (err.response?.data?.detail || err.message));
    }
  };
  
  const handleBulkDeleteNetworkBroadcasts = async () => {
    if (selectedBroadcasts.size === 0) return;
    if (!window.confirm(`Delete ${selectedBroadcasts.size} selected broadcast(s)?`)) return;
    try {
      await Promise.all(
        Array.from(selectedBroadcasts).map(id =>
          axios.delete(`${API}/conference-networks/${network.channel_id}/broadcasts/${id}`, { headers: getAuthHeaders() })
        )
      );
      setSelectedBroadcasts(new Set());
      fetchNetworkBroadcasts();
    } catch (err) {
      alert("Some deletions failed");
      fetchNetworkBroadcasts();
    }
  };

  const previewStandings = async () => {
    if (!newStanding.source_url) {
      alert("Please enter a source URL");
      return;
    }
    setPreviewing(true);
    setPreviewData(null);
    try {
      const res = await axios.post(`${API}/standings/preview`, { url: newStanding.source_url }, { headers: getAuthHeaders() });
      setPreviewData(res.data);
    } catch (err) {
      alert("Error previewing: " + (err.response?.data?.detail || err.message));
    } finally {
      setPreviewing(false);
    }
  };
  
  const addStandingsConfig = async () => {
    if (!newStanding.sport || !newStanding.source_url) {
      alert("Sport name and source URL are required");
      return;
    }
    if (!network?.channel_id) {
      alert("Please save the network first before adding standings");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/standings/configs`, {
        conference_id: network.channel_id,
        sport: newStanding.sport,
        source_url: newStanding.source_url
      }, { headers: getAuthHeaders() });
      
      setStandingsConfigs([...standingsConfigs, res.data.config]);
      setNewStanding({ sport: "", source_url: "" });
      setPreviewData(null);
    } catch (err) {
      alert("Error adding standings: " + (err.response?.data?.detail || err.message));
    }
  };
  
  const deleteStandingsConfig = async (configId) => {
    if (!confirm("Delete this standings configuration?")) return;
    try {
      await axios.delete(`${API}/standings/configs/${configId}`, { headers: getAuthHeaders() });
      setStandingsConfigs(standingsConfigs.filter(c => c.id !== configId));
    } catch (err) {
      alert("Error deleting: " + (err.response?.data?.detail || err.message));
    }
  };
  
  const syncStandings = async (configId) => {
    try {
      await axios.post(`${API}/standings/configs/${configId}/sync`, {}, { headers: getAuthHeaders() });
      alert("Standings synced successfully!");
    } catch (err) {
      alert("Error syncing: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug) {
      alert("Name and slug are required");
      return;
    }

    setSaving(true);
    try {
      let response;
      if (network) {
        response = await axios.put(
          `${API}/conference-networks/${network.id}`,
          formData,
          { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
        );
      } else {
        response = await axios.post(
          `${API}/conference-networks`,
          formData,
          { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
        );
      }
      onSave(response.data);
    } catch (err) {
      alert("Error saving: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const addSchool = () => {
    setFormData({
      ...formData,
      member_schools: [
        ...formData.member_schools,
        { id: `temp-${Date.now()}`, name: "", abbreviation: "", logo_url: "", athletic_website: "", order: formData.member_schools.length }
      ]
    });
  };

  const updateSchool = (index, field, value) => {
    const updated = [...formData.member_schools];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, member_schools: updated });
  };

  const removeSchool = (index) => {
    setFormData({
      ...formData,
      member_schools: formData.member_schools.filter((_, i) => i !== index)
    });
  };

  const addMenuItem = () => {
    setFormData({
      ...formData,
      menu_items: [
        ...formData.menu_items,
        { id: `temp-${Date.now()}`, label: "", href: "", type: "internal", order: formData.menu_items.length }
      ]
    });
  };

  const updateMenuItem = (index, field, value) => {
    const updated = [...formData.menu_items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, menu_items: updated });
  };

  const removeMenuItem = (index) => {
    setFormData({
      ...formData,
      menu_items: formData.menu_items.filter((_, i) => i !== index)
    });
  };

  const toggleSource = (sourceId) => {
    const current = formData.syndication_source_ids;
    if (current.includes(sourceId)) {
      setFormData({ ...formData, syndication_source_ids: current.filter(id => id !== sourceId) });
    } else {
      setFormData({ ...formData, syndication_source_ids: [...current, sourceId] });
    }
  };

  return (
    <div className="bg-zinc-800/50 border border-white/10 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-white font-bold uppercase tracking-wider">
          {network ? "Edit Conference Network" : "Create Conference Network"}
        </h3>
        <button onClick={onCancel} className="text-zinc-500 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 overflow-x-auto">
        {["basic", "schools", "menu", "sources", "standings", "scheduler"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveSection(tab)}
            className={`px-4 py-3 text-sm font-medium uppercase tracking-wider transition-colors whitespace-nowrap ${
              activeSection === tab
                ? "text-orange-500 border-b-2 border-orange-500"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            {tab === "basic" && "Basic Info"}
            {tab === "schools" && "Member Schools"}
            {tab === "menu" && "Menu Items"}
            {tab === "sources" && "Data Sources"}
            {tab === "standings" && "Standings"}
            {tab === "scheduler" && "Scheduler"}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Basic Info Section */}
        {activeSection === "basic" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Network Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Red River Conference Network"
                  className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  URL Slug *
                </label>
                <div className="flex items-center">
                  <span className="text-zinc-500 text-sm mr-1">/network/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                    placeholder="rrac"
                    className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Logo URL
              </label>
              <input
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Primary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-10 h-10 bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Secondary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="w-10 h-10 bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* Fallback Video Loop */}
            <div className="mt-4 p-4 bg-zinc-900/50 border border-white/10 rounded-lg">
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Fallback Video Loop
              </label>
              <p className="text-zinc-500 text-xs mb-3">
                This video plays when there are no live events. Enter a YouTube URL or direct video link.
              </p>
              <input
                type="url"
                value={formData.fallback_video_url}
                onChange={(e) => setFormData({ ...formData, fallback_video_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                data-testid="fallback-video-input"
              />
              {formData.fallback_video_url && (
                <div className="mt-2 text-green-500 text-xs flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  Fallback video configured
                </div>
              )}
            </div>
          </div>
        )}
        {activeSection === "schools" && (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">
              Add member schools. Their logos will appear at the top of the network page, linked to their athletic websites.
            </p>

            {formData.member_schools.map((school, index) => (
              <div key={school.id} className="bg-zinc-900/50 p-3 rounded space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={school.name}
                    onChange={(e) => updateSchool(index, "name", e.target.value)}
                    placeholder="School Name"
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    value={school.abbreviation}
                    onChange={(e) => updateSchool(index, "abbreviation", e.target.value)}
                    placeholder="Abbreviation (e.g., LSUS)"
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="url"
                    value={school.logo_url}
                    onChange={(e) => updateSchool(index, "logo_url", e.target.value)}
                    placeholder="Logo URL"
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <input
                    type="url"
                    value={school.athletic_website}
                    onChange={(e) => updateSchool(index, "athletic_website", e.target.value)}
                    placeholder="Athletic Website URL"
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                </div>
                <Button
                  onClick={() => removeSchool(index)}
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}

            <Button onClick={addSchool} className="bg-zinc-700 hover:bg-zinc-600 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add School
            </Button>
          </div>
        )}

        {/* Menu Items Section */}
        {activeSection === "menu" && (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">
              Configure the navigation menu. Default items (Schedule, Standings, Scores) are added automatically.
            </p>

            {formData.menu_items.map((item, index) => (
              <div key={item.id} className="bg-zinc-900/50 p-3 rounded space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) => updateMenuItem(index, "label", e.target.value)}
                    placeholder="Label"
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    value={item.href}
                    onChange={(e) => updateMenuItem(index, "href", e.target.value)}
                    placeholder="/path or https://..."
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <select
                    value={item.type}
                    onChange={(e) => updateMenuItem(index, "type", e.target.value)}
                    className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                  >
                    <option value="internal">Internal Link</option>
                    <option value="external">External Link</option>
                  </select>
                </div>
                <Button
                  onClick={() => removeMenuItem(index)}
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}

            <Button onClick={addMenuItem} className="bg-zinc-700 hover:bg-zinc-600 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Menu Item
            </Button>
          </div>
        )}

        {/* Data Sources Section */}
        {activeSection === "sources" && (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">
              Link syndication sources to this conference. Games from these sources will appear in the conference schedule.
            </p>

            {syndicationSources.length === 0 ? (
              <div className="text-center py-6 text-zinc-500">
                <Link2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No syndication sources available</p>
                <p className="text-xs">Add sources in the Syndication tab first</p>
              </div>
            ) : (
              <div className="space-y-2">
                {syndicationSources.map((source) => (
                  <label
                    key={source.id}
                    className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${
                      formData.syndication_source_ids.includes(source.id)
                        ? "bg-orange-500/20 border border-orange-500/50"
                        : "bg-zinc-900/50 border border-transparent hover:bg-zinc-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.syndication_source_ids.includes(source.id)}
                      onChange={() => toggleSource(source.id)}
                      className="w-4 h-4 accent-orange-500"
                    />
                    <div>
                      <p className="text-white font-medium">{source.name}</p>
                      <p className="text-zinc-500 text-xs truncate max-w-md">{source.hudl_fan_url}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Standings Section */}
        {activeSection === "standings" && (
          <div className="space-y-6">
            <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-4">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                <Table className="w-4 h-4" />
                Add Standings Source
              </h4>
              <p className="text-zinc-400 text-sm mb-4">
                Paste a link to a standings page (e.g., redriverconference.com/sports/bsb/standings) and enter the sport name.
                Standings will be automatically scraped every 5 hours.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Sport Name *
                  </label>
                  <input
                    type="text"
                    value={newStanding.sport}
                    onChange={(e) => setNewStanding({ ...newStanding, sport: e.target.value })}
                    placeholder="e.g., Baseball, Football, Basketball"
                    className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                    data-testid="standings-sport-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Source URL *
                  </label>
                  <input
                    type="url"
                    value={newStanding.source_url}
                    onChange={(e) => setNewStanding({ ...newStanding, source_url: e.target.value })}
                    placeholder="https://www.redriverconference.com/sports/bsb/2025-26/standings"
                    className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                    data-testid="standings-url-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={previewStandings}
                  disabled={previewing || !newStanding.source_url}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white"
                  data-testid="preview-standings-btn"
                >
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Preview
                </Button>
                <Button
                  onClick={addStandingsConfig}
                  disabled={!newStanding.sport || !newStanding.source_url || !network?.channel_id}
                  className="bg-orange-500 hover:bg-orange-600 text-black"
                  data-testid="add-standings-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Standings
                </Button>
              </div>
              
              {!network?.channel_id && (
                <p className="text-yellow-500 text-xs mt-2">
                  Save the network first before adding standings configurations.
                </p>
              )}
              
              {/* Preview Panel */}
              {previewData && (
                <div className="mt-4 border border-white/10 rounded overflow-hidden">
                  <div className="bg-zinc-800 px-3 py-2 text-xs font-bold text-zinc-400 uppercase">
                    Preview ({previewData.teams?.length || 0} teams found)
                  </div>
                  {previewData.error ? (
                    <div className="p-3 text-red-400 text-sm">{previewData.error}</div>
                  ) : previewData.teams?.length > 0 ? (
                    <div className="max-h-48 overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            {previewData.headers?.slice(0, 6).map((h, i) => (
                              <th key={i} className="px-3 py-2 text-left text-zinc-400 font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.teams.slice(0, 5).map((team, idx) => (
                            <tr key={idx} className="border-b border-white/5">
                              {team.raw_values?.slice(0, 6).map((val, i) => (
                                <td key={i} className="px-3 py-2 text-white">{val}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {previewData.teams.length > 5 && (
                        <div className="px-3 py-2 text-xs text-zinc-500">
                          + {previewData.teams.length - 5} more teams
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 text-zinc-500 text-sm">No teams found in the table</div>
                  )}
                </div>
              )}
            </div>
            
            {/* Existing Standings Configs */}
            <div>
              <h4 className="text-white font-bold mb-3">Configured Standings</h4>
              
              {standingsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : standingsConfigs.length === 0 ? (
                <div className="text-center py-6 text-zinc-500 bg-zinc-900/50 rounded-lg border border-white/10">
                  <Table className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No standings configured yet</p>
                  <p className="text-xs">Add a standings source above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {standingsConfigs.map((config) => (
                    <div
                      key={config.id}
                      className="bg-zinc-900/50 border border-white/10 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-medium capitalize">{config.sport}</p>
                        <p className="text-zinc-500 text-xs truncate max-w-md">{config.source_url}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => syncStandings(config.id)}
                          className="bg-zinc-700 hover:bg-zinc-600 text-white"
                          size="sm"
                          title="Sync now"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => deleteStandingsConfig(config.id)}
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scheduler Section */}
        {activeSection === "scheduler" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold uppercase tracking-wider text-sm">Network Schedule</h3>
              <div className="flex items-center gap-2">
                {selectedBroadcasts.size > 0 && (
                  <Button onClick={handleBulkDeleteNetworkBroadcasts} className="bg-red-600 hover:bg-red-500 text-white text-xs" size="sm" data-testid="network-bulk-delete">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete {selectedBroadcasts.size}
                  </Button>
                )}
                <Button onClick={() => setShowAddGame(!showAddGame)} className="bg-orange-500 hover:bg-orange-600 text-black text-xs" size="sm" data-testid="network-add-game-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Game
                </Button>
              </div>
            </div>

            {showAddGame && (
              <div className="bg-zinc-800/50 border border-white/10 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input value={newGame.title} onChange={e => setNewGame({...newGame, title: e.target.value})} placeholder="Game Title (e.g. Team A @ Team B)" className="col-span-2 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-title" />
                  <select value={newGame.sport} onChange={e => setNewGame({...newGame, sport: e.target.value})} className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-sport">
                    <option>Basketball</option><option>Football</option><option>Soccer</option><option>Volleyball</option><option>Baseball</option><option>Softball</option><option>Hockey</option><option>Wrestling</option><option>Track</option><option>Tennis</option><option>Golf</option><option>Swimming</option><option>Lacrosse</option>
                  </select>
                  <input type="date" value={newGame.scheduled_date} onChange={e => setNewGame({...newGame, scheduled_date: e.target.value})} className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-date" />
                  <input type="time" value={newGame.scheduled_time} onChange={e => setNewGame({...newGame, scheduled_time: e.target.value})} className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-time" />
                  <input value={newGame.home_team_name} onChange={e => setNewGame({...newGame, home_team_name: e.target.value})} placeholder="Home Team" className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-home" />
                  <input value={newGame.away_team_name} onChange={e => setNewGame({...newGame, away_team_name: e.target.value})} placeholder="Away Team" className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-away" />
                  <input value={newGame.youtube_url} onChange={e => setNewGame({...newGame, youtube_url: e.target.value})} placeholder="YouTube URL (optional)" className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-youtube" />
                  <input value={newGame.stats_link} onChange={e => setNewGame({...newGame, stats_link: e.target.value})} placeholder="Stats Link (optional)" className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="network-game-stats" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button onClick={() => setShowAddGame(false)} className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs" size="sm">Cancel</Button>
                  <Button onClick={handleAddGame} className="bg-green-600 hover:bg-green-500 text-white text-xs" size="sm" data-testid="network-game-save">Save Game</Button>
                </div>
              </div>
            )}

            {schedulerLoading ? (
              <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading...</div>
            ) : networkBroadcasts.length === 0 ? (
              <p className="text-zinc-500 text-sm py-6 text-center">No games scheduled for this network yet.</p>
            ) : (
              <div className="border border-white/5">
                <div className="grid grid-cols-[30px_auto_1fr_100px_90px_80px_auto] gap-2 items-center px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 bg-zinc-900">
                  <div className="flex items-center justify-center">
                    <input type="checkbox" checked={networkBroadcasts.length > 0 && selectedBroadcasts.size === networkBroadcasts.length} onChange={() => {
                      if (selectedBroadcasts.size === networkBroadcasts.length) setSelectedBroadcasts(new Set());
                      else setSelectedBroadcasts(new Set(networkBroadcasts.map(b => b.id)));
                    }} className="w-3.5 h-3.5 accent-orange-500 cursor-pointer" />
                  </div>
                  <div className="w-16">Status</div><div>Game</div><div>Sport</div><div>Date</div><div>Time</div><div className="w-16">Actions</div>
                </div>
                {networkBroadcasts.map(b => (
                  <div key={b.id} className={`grid grid-cols-[30px_auto_1fr_100px_90px_80px_auto] gap-2 items-center px-3 py-2 text-xs border-t border-white/5 hover:bg-zinc-800/30 ${selectedBroadcasts.has(b.id) ? 'bg-orange-500/10' : ''}`}>
                    <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedBroadcasts.has(b.id)} onChange={() => {
                        setSelectedBroadcasts(prev => { const n = new Set(prev); if (n.has(b.id)) n.delete(b.id); else n.add(b.id); return n; });
                      }} className="w-3.5 h-3.5 accent-orange-500 cursor-pointer" />
                    </div>
                    <div className="w-16">
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${b.is_live ? 'bg-red-500 text-white' : b.is_completed ? 'bg-green-600/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                        {b.is_live ? 'LIVE' : b.is_completed ? 'DONE' : 'SCHED'}
                      </span>
                    </div>
                    <div className="text-white truncate">{b.title}</div>
                    <div className="text-zinc-400">{b.sport}</div>
                    <div className="text-zinc-400">{b.scheduled_date ? new Date(b.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', {month:'short',day:'numeric'}) : '-'}</div>
                    <div className="text-zinc-400">{b.scheduled_time || '-'}</div>
                    <div className="w-16">
                      <button onClick={() => handleDeleteNetworkBroadcast(b.id)} className="text-red-400 hover:text-red-300" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-white/10 flex justify-end gap-3">
        <Button onClick={onCancel} className="bg-zinc-700 hover:bg-zinc-600 text-white">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-black"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {network ? "Save Changes" : "Create Network"}
        </Button>
      </div>
    </div>
  );
};

export default ConferenceNetworkTab;
